import { DateTime } from 'luxon';
import { GiftCard } from './gift-card';
import { Product, ProductPackageOption } from './product';
import { ShippingInterval } from './shipping';

// Signaturgruppen token session, our MitID provider
export const sgTokenSessionKey = 'sgAgeVerificationToken';

export type LineMap = Record<number, ProductLine | InnerLineMap>;

export type InnerLineMap = Record<number, ProductLine>;

export const isProductLine = (line: ProductLine | InnerLineMap): line is ProductLine => {
  return (line as ProductLine)?.productId !== undefined;
};

export const isProductLineMap = (line: ProductLine | InnerLineMap): line is InnerLineMap => {
  if (!line) {
    return false;
  }

  for (const [key, value] of Object.entries(line)) {
    if (isProductLine(value)) {
      return true;
    }
  }

  return false;
};

export interface PostProductLine {
  productName?: string;
  productNumber: number;
  quantity: number;
  total?: number;
  discountTotal?: number;
}

export interface ProductLine {
  lineId: number;
  productId: number;
  quantity: number;
  price: number;
  discount: number;
  moduleLines: ProductLine[];
  giftCardInfo?: GiftCard;

  changes?: ProductChanges;
  product?: Product;
  error?: boolean;
  packageOptions?: ProductPackageOption[];
}

export interface ProductChanges {
  from: string; // TODO ENUM EVERYTHING
  to: string;
  change: string;
}

export interface CalculateBasketRequest {
  merchantId: number;
  orderlines: ProductLine[];
  shipping: ShippingVM;
  discountCode: string;
  customerId?: number;
  customerPackaging?: boolean;
  giftCardToken?: string;
}

export interface CalculateBasketResponse {
  orderlines: ProductLine[];
  packaging: Packaging;
  orderTotal: number;
  linesTotal: number;
  shippingPrice: number;
  codeDiscount: number;
  containsAlcohol: boolean;
  errors: CalculateBasketError[];
  remainingGiftCardValue: number;
  giftCardDiscount: number;
}

export interface CalculateBasketError {
  entityIds: number[];
  entityType: EntityType;
  errorMessage: string;
  responseCode: number;
  price: number;
}

export enum EntityType {
  Unknown = 0,
  Shipping,
  Product,
  Price,
}

export interface Packaging {
  cost: number;
  defaultDetails: PackagingDetails;
  thermalDetails: PackagingDetails;
}

export interface PackagingDetails {
  priceModel: PriceModel;
  cost: number;
  count: number;
}

export enum PriceModel {
  modelA,
  modelB,
}

export interface ShippingOption {
  id: number;
  name: string;
  subTitle: string;
  membersOnly: boolean;
  price: number;
  fixedPrice: boolean;
  giftCardShipping: boolean;
  displayThroughExclusiveProducts: boolean;
  exclusiveProductInBasket?: boolean;
}

export interface Shipping {
  shippingType: number | null;
  date: DateTime;
  time: ShippingInterval;
  price: number;
  address?: string;
  zipCode?: string;
  city?: string;
  isWolt?: boolean;
  woltUsePriceGroups?: boolean;
  woltOpenDays?: number;
  fastestPossible?: boolean;
}

export interface ShippingVM {
  shippingType: number | null;
  deliveryDate: string;
  deliveryTime: string;
  price: number;
  address?: string;
  zipCode?: string;
  city?: string;
  isWolt?: boolean;
}

export interface CustomerDetails {
  tradeAgreement: boolean;
  newsletterConsent: boolean;
  alternativeShipping: boolean;
  alternativeReclaimAddress?: boolean;
  customerPackaging: boolean;
  legalAgeConsent: boolean;
  isBusiness: boolean;
  payment: PaymentDetails;
  shipping: ShippingDetails;
  reclaim?: ReclaimDetails;
}

export interface PaymentDetails {
  firstName: string;
  lastName: string;
  email: string;
  telephone: string;
  companyName: string | null;
  CVR: string | null;
  EAN: string | null;
  requisition: string | null;
  address: string;
  zip: string;
  city: string;
  note: string | null;
  invoiceRecipientName: string | null;
  invoiceRecipientEmail: string | null;
}

export interface ShippingDetails {
  firstName: string;
  lastName: string;
  telephone: string;
  companyName?: string;
  address: string;
  zip: string;
  city: string;
}

export interface ReclaimDetails {
  email: string;
  telephone: string;
  address: string;
  zip: string;
  city: string;
}

export interface CloseDeliveryInterval {
  id: number;
  start: string;
  end: string;
  date: string;
  endDate: string;
  isDayClosed: boolean;
  shippingIds: number[];
}

export interface PromotionCode {
  promotionCode: string;
}

export interface BasketTableColumns {
  name: string;
  price: string;
  total: string;
  quantity: string;
  delete: string;
}
